import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { InstitutionCodingsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsInstitutionsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as InstitutionCodingActions from "../../../actions/institutionCoding";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { InstitutionLookupsLayout } from "../../index";

import "./InstitutionCodingsPage.scss";

interface InstitutionCodingsPageProps {
    institutionCodings: Dtos.InstitutionCoding[];
    formFilter: IFormFilter;
    loadingInstitutionCodings: boolean;
    loadInstitutionCodingsSuccess: boolean;
    loadInstitutionCodingsFailure: boolean;
    loadInstitutionCodingsErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearInstitutionCodings: InstitutionCodingActions.IInstitutionCodingClearActionCreator;
    loadInstitutionCodings: InstitutionCodingActions.IInstitutionCodingLoadActionCreator;
    filter: InstitutionCodingActions.IInstitutionCodingFilterActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const InstitutionCodingsPage: FC<InstitutionCodingsPageProps> = ({
    institutionCodings,
    formFilter,
    loadingInstitutionCodings,
    loadInstitutionCodingsSuccess,
    loadInstitutionCodingsFailure,
    loadInstitutionCodingsErrorCode,
    permissions,
    pageSize,
    clearInstitutionCodings,
    loadInstitutionCodings,
    filter,
    setPageSize,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showInstitutionCodings =
        !loadingInstitutionCodings && loadInstitutionCodingsSuccess;


    // Load site locations
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsInstitutionsSubMenu(permissions));
            setBreadcrumbs(InstitutionCodingsBreadcrumbs());

            loadInstitutionCodings();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsInstitutionsSubMenu,
        permissions,
        setBreadcrumbs,
        InstitutionCodingsBreadcrumbs,
        loadInstitutionCodings,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionCodingsFailure) {
            navigate(`/error/${loadInstitutionCodingsErrorCode}`);
        }
    }, [
        loadInstitutionCodingsFailure,
        loadInstitutionCodingsErrorCode,
        navigate,
    ]);

    // Cleanup effect to clear the institution codings on component unmount
    useEffect(() => {
        return () => {
            clearInstitutionCodings();
        };
    }, [clearInstitutionCodings]);

    const createTable = useMemo(() => {
        if (!institutionCodings || institutionCodings.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no institution codings to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = institutionCodings.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={institutionCodings}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : institutionCodings.length
                    }
                    pageSize={
                        showPagination ? pageSize : institutionCodings.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no institution codings to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const url: string = `/settings/institution/institutioncodings/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [
        institutionCodings,
        pageSize,
        PAGE_SIZE_OPTIONS,
        setPageSize,
        navigate,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Institution Codings</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.InstitutionCodingCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={
                                    "/settings/institution/institutioncodings/create"
                                }
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showInstitutionCodings ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showInstitutionCodings ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showInstitutionCodings, createTable]);

    return (
        <InstitutionLookupsLayout
            loading={loadingInstitutionCodings}
            permission={Dtos.Permission.InstitutionCodingView}
        >
            {renderContent}
        </InstitutionLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionCodingsPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institutionCodings: state.institutionCodings.formFilteredData!,
        formFilter: state.institutionCodings.formFilter,
        loadingInstitutionCodings:
            state.institutionCodings.loadState &&
            state.institutionCodings.loadState.status == RequestState.Pending,
        loadInstitutionCodingsSuccess:
            state.institutionCodings.loadState &&
            state.institutionCodings.loadState.status == RequestState.Success,
        loadInstitutionCodingsFailure:
            state.institutionCodings.loadState &&
            state.institutionCodings.loadState.status == RequestState.Failure,
        loadInstitutionCodingsErrorCode:
            state.institutionCodings.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<InstitutionCodingsPageProps> => {
    return {
        loadInstitutionCodings: bindActionCreators(
            InstitutionCodingActions.LoadInstitutionCoding,
            dispatch
        ),
        clearInstitutionCodings: bindActionCreators(
            InstitutionCodingActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(InstitutionCodingActions.Filter, dispatch),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InstitutionCodingsPage);
